import { useMutation } from "@apollo/client";
import {
  CreateUploadFileInput,
  uploadUrlResponse,
} from "../../../types/graphQlQuery.interface";
import {
  ADD_PROJECT_MATERIAL_MEASUREMENT,
  APPROVE_SCOPE_MUTATION,
  ARCHIVE_PROJECT_FILE,
  CREATE_ADDRESS,
  CREATE_PROJECT,
  CREATE_PROJECT_FILE,
  CREATE_PROJECT_PAYMENT_ADMIN,
  CREATE_UPLOAD_URL,
  UPDATE_PROJECT_FILE,
  UPDATE_PROJECT_PAYMENT_ADMIN,
  UPDATE_STAGE_STATUS,
} from "../../operation/mutations";
import {
  ApproveScopeResponse,
  ScopeApprovalInput,
} from "../../../redux/types/InsuranceCompany";
import {
  AddProjectMaterialMeasurementResponse,
  ProjectMeasurementInput,
} from "../../../redux/types/projectDetails";
import { CreateProjectFileInput, ProjectPaymentAdmin, UpdateProjectFileInput, UpdateProjectPaymentAdmin } from "../../../redux/types/Project";

export const useCreateUploadUrl = () => {
  const [createContractorReportsMutation] = useMutation<
    uploadUrlResponse,
    { input: CreateUploadFileInput }
  >(CREATE_UPLOAD_URL);

  const createUploadUrl = async (input: CreateUploadFileInput) => {
    try {
      const { data } = await createContractorReportsMutation({
        variables: { input },
      });

      if (!data) {
        throw new Error("Data is null");
      }
      return data.createUploadUrl;
    } catch (error) {
      console.error("Error Creating Upload url:", error);
      return false;
    }
  };

  return { createUploadUrl };
};

export const useApproveProjectScope = () => {
  const [ApproveScope] = useMutation<
    ApproveScopeResponse,
    { input: ScopeApprovalInput }
  >(APPROVE_SCOPE_MUTATION);
  const approveScope = async (input: ScopeApprovalInput): Promise<boolean> => {
    try {
      const { data } = await ApproveScope({
        variables: { input },
      });
      if (!data) {
        throw new Error("Data is null");
      }
      return data.approveScope;
    } catch (error) {
      console.error("Error approving scope:", error);
      return false;
    }
  };
  return {
    approveScope,
  };
};

export const useAddProjectMaterialMeasurement = () => {
  const [addProjectMeasurementMutation, { data, loading, error }] = useMutation<
    AddProjectMaterialMeasurementResponse,
    { input: ProjectMeasurementInput[] }
  >(ADD_PROJECT_MATERIAL_MEASUREMENT);

  const addProjectMaterialMeasurement = async (
    input: ProjectMeasurementInput[]
  ) => {
    return await addProjectMeasurementMutation({
      variables: { input },
    });
  };

  return {
    addProjectMaterialMeasurement,
    data,
    loading,
    error,
  };
};

export const useCreateProjectFile = () => {
  const [createProjectFileMutation] = useMutation<
    { createProjectFile: boolean },
    { input: CreateProjectFileInput }
  >(CREATE_PROJECT_FILE);

  const createProjectFile = async (
    input: CreateProjectFileInput
  ): Promise<boolean> => {
    try {
      const { data } = await createProjectFileMutation({
        variables: { input },
      });
      if (!data?.createProjectFile) {
        throw new Error(
          "Failed to create project file: Project file data is not available"
        );
      }
      return data.createProjectFile;
    } catch (error) {
      console.error("Error creating project file:", error);
      throw error;
    }
  };

  return { createProjectFile };
};

export const useUpdateProjectFile = () => {
  const [updateProjectFileMutation] = useMutation<
    { updateProjectFile: boolean },
    { input: UpdateProjectFileInput }
  >(UPDATE_PROJECT_FILE);

  const updateProjectFile = async (
    input: UpdateProjectFileInput
  ): Promise<boolean> => {
    try {
      const { data } = await updateProjectFileMutation({
        variables: { input },
      });
      if (!data?.updateProjectFile) {
        throw new Error(
          "Failed to update project file: Project file data is not available"
        );
      }
      return data.updateProjectFile;
    } catch (error) {
      console.error("Error updating project file:", error);
      throw error;
    }
  };
  return { updateProjectFile };
};

export const useArchiveProjectFile = () => {
  const [archiveProjectFileMutation] = useMutation<
    { ArchiveProjectFile: boolean },
    { pId: number; fId: number }
  >(ARCHIVE_PROJECT_FILE);

  const archiveProjectFile = async (
    pId: number,
    fId: number
  ): Promise<boolean> => {
    try {
      const { data } = await archiveProjectFileMutation({
        variables: { pId, fId },
      });
      if (!data?.ArchiveProjectFile) {
        throw new Error(
          "Failed to archive project file: Project file data is not available"
        );
      }
      return data.ArchiveProjectFile;
    } catch (error) {
      console.error("Error archiving project file:", error);
      throw error;
    }
  };

  return { archiveProjectFile };
};

export const useCreateProjectPayment = () => {
  const [createProjectPaymentMutation] = useMutation<
    { createProjectPaymentAdmin: boolean },
    { input: ProjectPaymentAdmin }
  >(CREATE_PROJECT_PAYMENT_ADMIN);

  const createProjectPayment = async (
    input: ProjectPaymentAdmin
  ): Promise<boolean> => {
    try {
      const { data } = await createProjectPaymentMutation({
        variables: { input },
      });
      if (!data?.createProjectPaymentAdmin) {
        throw new Error(
          "Failed to create project payment: Project payment data is not available"
        );
      }
      return data.createProjectPaymentAdmin;
    } catch (error) {
      console.error("Error creating project payment:", error);
      throw error;
    }
  }
  return { createProjectPayment };
};

export const useUpdateProjectPayment = () => {
  const [updateProjectPaymentMutation] = useMutation<
    { updateProjectPaymentAdmin: boolean },
    { input: UpdateProjectPaymentAdmin }
  >(UPDATE_PROJECT_PAYMENT_ADMIN);

  const updateProjectPayment = async (
    input: UpdateProjectPaymentAdmin
  ): Promise<boolean> => {
    try {
      const { data } = await updateProjectPaymentMutation({
        variables: { input },
      });
      if (!data?.updateProjectPaymentAdmin) {
        throw new Error(
          "Failed to update project payment: Project payment data is not available"
        );
      }
      return data.updateProjectPaymentAdmin;
    } catch (error) {
      console.error("Error updating project payment:", error);
      throw error;
    }
  }
  return { updateProjectPayment };
};

export const useCreateAddress = () => {
  const [createAddressMutation] = useMutation<
    { createAddress: { id: number; success: string } },
    { input: any }
  >(CREATE_ADDRESS);

  const createAddress = async (input: any) => {
    try {
      const { data } = await createAddressMutation({
        variables: { input },
      });
      if (!data?.createAddress) {
        throw new Error(
          "Failed to create address: Address data is not available"
        );
      }
      return data.createAddress;
    } catch (error) {
      console.error("Error creating address:", error);
      throw error;
    }
  };

  return { createAddress };
};

export const useCreateProject = () => {
  const [createProjectMutation] = useMutation<
    { createProject: { id: number; projectRefNumber: string } },
    { input: any }
  >(CREATE_PROJECT);

  const createProject = async (input: any) => {
    try {
      const { data } = await createProjectMutation({
        variables: { input },
      });
      if (!data?.createProject) {
        throw new Error(
          "Failed to create project: Project data is not available"
        );
      }
      return data.createProject;
    } catch (error) {
      console.error("Error creating project:", error);
      throw error;
    }
  };

  return { createProject };
};

export const useUpdateStageStatus = () => {
  const [updateStageMutation, { loading }] = useMutation<
    { UpdateStageStatus: boolean },
    { projectId: number; stage: string; completed: boolean }
  >(UPDATE_STAGE_STATUS);

  const updateStageStatus = async (
    projectId: number,
    stage: string,
    completed: boolean
  ): Promise<boolean> => {
    try {
      const { data } = await updateStageMutation({
        variables: { projectId, stage, completed },
      });
      return data?.UpdateStageStatus ?? false;
    } catch (error) {
      console.error("Error updating stage status:", error);
      return false;
    }
  };

  return { updateStageStatus, loading };
};